import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { USER_LOAN_PROGRAM } from '@/config/constants';

export type RefinanceRefiType =
  | 'rate_and_term'
  | 'cash_out'
  | 'streamline'
  | 'heloc'
  | 'heloan'
  | 'second_mortgage';

export type RefinanceCurrentLoan = {
  rate: number;
  balance: number;
  termYears: number;
  monthlyPayment: number;
  escrowTaxes: number;
  escrowInsurance: number;
  escrowHoa: number;
};

export type RefinanceProperty = {
  value: number;
  address: string;
  city: string;
  state: string;
  propertyType: string;
  zipCode: string;
};

export type RefinanceNewLoan = {
  termYears: number;
  creditScore: number;
  cashOutAmount: number;
  requestedLoanAmount: number;
};

export type RefinanceSelection = {
  program: USER_LOAN_PROGRAM;
  refiType: RefinanceRefiType;
};

export type RefinanceStatementFields = {
  escrowBalance: number;
  buydownBalance: number;
  prepaymentPenalty: number;
  lastPaymentDate: string;
  estimatedPayoff: number;
};

export type RefinanceState = {
  currentLoan: RefinanceCurrentLoan;
  property: RefinanceProperty;
  newLoan: RefinanceNewLoan;
  selection: RefinanceSelection;
  statement: RefinanceStatementFields;
};

const initialState: RefinanceState = {
  currentLoan: {
    rate: 6.75,
    balance: 250000,
    termYears: 30,
    monthlyPayment: 1800,
    escrowTaxes: 300,
    escrowInsurance: 120,
    escrowHoa: 0,
  },
  property: {
    value: 350000,
    address: '',
    city: '',
    state: '',
    propertyType: 'Single Family',
    zipCode: '',
  },
  newLoan: {
    termYears: 30,
    creditScore: 720,
    cashOutAmount: 0,
    requestedLoanAmount: 0,
  },
  selection: {
    program: USER_LOAN_PROGRAM.CONVENTIONAL,
    refiType: 'rate_and_term',
  },
  statement: {
    escrowBalance: 0,
    buydownBalance: 0,
    prepaymentPenalty: 0,
    lastPaymentDate: '',
    estimatedPayoff: 0,
  },
};

type SetFieldPayload<T> = {
  field: keyof T;
  value: T[keyof T];
};

const refinanceSlice = createSlice({
  name: 'refinance',
  initialState,
  reducers: {
    setCurrentLoanField: (
      state,
      action: PayloadAction<SetFieldPayload<RefinanceCurrentLoan>>
    ) => {
      state.currentLoan[action.payload.field] = action.payload.value;
    },
    setPropertyField: (
      state,
      action: PayloadAction<SetFieldPayload<RefinanceProperty>>
    ) => {
      state.property[action.payload.field] = action.payload.value;
    },
    setNewLoanField: (
      state,
      action: PayloadAction<SetFieldPayload<RefinanceNewLoan>>
    ) => {
      state.newLoan[action.payload.field] = action.payload.value;
    },
    setRefinanceSelection: (
      state,
      action: PayloadAction<Partial<RefinanceSelection>>
    ) => {
      state.selection = { ...state.selection, ...action.payload };
    },
    setStatementField: (
      state,
      action: PayloadAction<SetFieldPayload<RefinanceStatementFields>>
    ) => {
      state.statement[action.payload.field] = action.payload.value;
    },
    setRefinanceState: (
      _state,
      action: PayloadAction<Partial<RefinanceState>>
    ) => {
      return { ...initialState, ...action.payload };
    },
    resetRefinanceState: () => initialState,
  },
});

export const {
  setCurrentLoanField,
  setPropertyField,
  setNewLoanField,
  setRefinanceSelection,
  setStatementField,
  setRefinanceState,
  resetRefinanceState,
} = refinanceSlice.actions;

export default refinanceSlice.reducer;
